import * as React from 'react'
import { observer } from 'mobx-react'
import {
  Dialog,
  DialogButton,
  DialogButtonGroup,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/Dialog'
import { Form, TextAreaContainer } from 'components/inputs'
import Checkbox from 'components/Checkbox'
import { ContentLabel } from 'components/ContentLabel'
import Pill from 'components/Pill'
import AddJobNoteDialogViewModel from './AddJobNoteDialogViewModel'
import styles from './AddJobNoteDialog.sass'

interface Props {
  vm: AddJobNoteDialogViewModel
}

// Show remaining chars when approaching the limit.
const COUNTER_THRESHOLD = 200

export default observer(function AddJobNoteDialog({ vm }: Props) {
  return (
    <Dialog showing={vm.showing} onClose={!vm.sendNote.pending && vm.close}>
      <Form onSubmit={() => vm.sendNote()}>
        <DialogHeader>
          <DialogTitle>Add Job Note</DialogTitle>
          <DialogDescription>
            Capture context, updates, or decisions for this job.
          </DialogDescription>
        </DialogHeader>
        <DialogContent>
          <TextAreaContainer
            autoFocus
            data-private
            rows={5}
            label="Note"
            help="Add any relevant details or context. Notes can be seen by anyone with access to the job, minus the client."
            model={vm}
            field="composerText"
            disabled={vm.sendNote.pending}
            maxLength={vm.MAX_NOTE_LENGTH}
            data-test="add-job-note-input"
          />
          {vm.noteCharsRemaining <= COUNTER_THRESHOLD && (
            <span className={styles.charCount}>
              {vm.noteCharsRemaining} characters remaining
            </span>
          )}
          {vm.recipients.length > 0 && (
            <div className={styles.notify}>
              <ContentLabel>Notify</ContentLabel>
              {vm.recipients.map((recipient) => (
                <label
                  key={recipient.userPublicId}
                  className={styles.notifyOption}
                >
                  <Checkbox
                    checked={recipient.selected}
                    disabled={vm.sendNote.pending}
                    onChange={(checked) =>
                      vm.toggleRecipient(recipient.userPublicId, checked)
                    }
                    data-test="add-job-note-notify"
                  />
                  <span className={styles.notifyName}>
                    {recipient.name}
                    <Pill shape="square" trim>
                      {recipient.role}
                    </Pill>
                  </span>
                </label>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogFooter bordered>
          <DialogButtonGroup>
            <DialogButton
              disabled={vm.sendNote.pending}
              onClick={vm.close}
              type="default"
            >
              Cancel
            </DialogButton>
            <DialogButton
              submit
              loading={vm.sendNote.pending}
              disabled={!vm.canSend}
              type="primary"
              dataTest="add-job-note-confirm"
            >
              Publish Note
            </DialogButton>
          </DialogButtonGroup>
        </DialogFooter>
      </Form>
    </Dialog>
  )
})
